import { isSafeUrl } from './block-builder';

/**
 * Render SEGURO del rich text de bloques `text` (negrita/cursiva/subrayado/
 * links/listas). El backend guarda `block.html` ya sanitizado con la misma
 * whitelist, pero acá NO confiamos en eso: re-validamos al renderizar.
 *
 * Cómo mantiene la garantía "sin innerHTML" del renderer (regla 8 del
 * CLAUDE.md): el string se parsea con `DOMParser.parseFromString`, que produce
 * un documento INERTE — sus scripts no ejecutan y nada toca el DOM vivo —, y
 * el árbol resultante se RECONSTRUYE a mano: cada nodo cuyo tag está en la
 * whitelist se re-crea con `createElement`/`createTextNode`; los atributos NO
 * se copian (salvo `href` validado con `isSafeUrl`); los tags desconocidos se
 * "unwrappean" (se descarta el elemento, se conservan sus hijos de texto).
 * Nunca se inserta HTML crudo en el documento real.
 */

/** Tags permitidos → tag con el que se re-crean (b/i se normalizan). */
const ALLOWED_TAGS: Record<string, string> = {
  P: 'p',
  BR: 'br',
  STRONG: 'strong',
  B: 'strong',
  EM: 'em',
  I: 'em',
  U: 'u',
  S: 's',
  UL: 'ul',
  OL: 'ol',
  LI: 'li',
  A: 'a',
};

/**
 * Tags que se descartan CON su contenido (a diferencia del unwrap): su texto
 * interno es código/markup, no copy — dejarlo visible sería basura ("window.x
 * = 1;" pintado en la guía).
 */
const DROP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'TEMPLATE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'NOSCRIPT',
  'TITLE',
  'TEXTAREA',
  'SELECT',
  'SVG',
  'MATH',
]);

const MAX_INPUT_LEN = 16 * 1024;
const MAX_DEPTH = 20;
const MAX_NODES = 1000;

/**
 * Convierte un string de rich text whitelisted en un `DocumentFragment` seguro
 * construido nodo a nodo. Si el input excede las guardas (tamaño/nodos), cae a
 * un nodo de texto con el contenido plano — nunca falla ni lanza.
 */
export function renderRichText(html: string, doc: Document): DocumentFragment {
  const fragment = doc.createDocumentFragment();
  if (typeof html !== 'string' || html.length === 0) return fragment;

  let parsed: Document;
  try {
    parsed = new DOMParser().parseFromString(
      html.length > MAX_INPUT_LEN ? html.slice(0, MAX_INPUT_LEN) : html,
      'text/html',
    );
  } catch {
    fragment.appendChild(doc.createTextNode(html));
    return fragment;
  }

  const budget = { nodes: 0, exceeded: false };
  for (const child of Array.from(parsed.body.childNodes)) {
    const rebuilt = rebuildNode(child, doc, 0, budget);
    if (rebuilt) fragment.appendChild(rebuilt);
  }

  // Presupuesto excedido: descartar el árbol parcial y caer a texto plano.
  if (budget.exceeded) {
    const plain = doc.createDocumentFragment();
    plain.appendChild(doc.createTextNode(parsed.body.textContent ?? ''));
    return plain;
  }
  return fragment;
}

function rebuildNode(
  node: Node,
  doc: Document,
  depth: number,
  budget: { nodes: number; exceeded: boolean },
): Node | null {
  if (budget.exceeded) return null;
  if (++budget.nodes > MAX_NODES || depth > MAX_DEPTH) {
    budget.exceeded = true;
    return null;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    return doc.createTextNode(node.textContent ?? '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null; // comentarios, etc.

  const el = node as Element;
  if (DROP_TAGS.has(el.tagName)) return null; // fuera con contenido y todo
  const mapped = ALLOWED_TAGS[el.tagName];

  // Tag desconocido → unwrap: se conservan los hijos, sin el elemento.
  if (!mapped) {
    const frag = doc.createDocumentFragment();
    for (const child of Array.from(el.childNodes)) {
      const rebuilt = rebuildNode(child, doc, depth + 1, budget);
      if (rebuilt) frag.appendChild(rebuilt);
    }
    return frag;
  }

  // Links: solo con href http(s); si el href no pasa, se unwrappea a texto.
  if (mapped === 'a') {
    const href = el.getAttribute('href') ?? '';
    if (!isSafeUrl(href)) {
      const frag = doc.createDocumentFragment();
      for (const child of Array.from(el.childNodes)) {
        const rebuilt = rebuildNode(child, doc, depth + 1, budget);
        if (rebuilt) frag.appendChild(rebuilt);
      }
      return frag;
    }
    const a = doc.createElement('a');
    a.setAttribute('href', href);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer nofollow');
    for (const child of Array.from(el.childNodes)) {
      const rebuilt = rebuildNode(child, doc, depth + 1, budget);
      if (rebuilt) a.appendChild(rebuilt);
    }
    return a;
  }

  // Elemento permitido: re-crear SIN copiar atributos.
  const rebuilt = doc.createElement(mapped);
  for (const child of Array.from(el.childNodes)) {
    const childNode = rebuildNode(child, doc, depth + 1, budget);
    if (childNode) rebuilt.appendChild(childNode);
  }
  return rebuilt;
}
